// ─────────────────────────────────────────────────────────────────────
// Stage definitions
//
// Internally the board tracks 9 states (each produces something real, so
// moves stay one step at a time). People see the 7 opportunity stages of the
// glossary (docs/ux/DECISIONS.md §5): Lead → Qualified → Quoted → Scheduled →
// In progress → Invoiced → Paid. Approved is shown inside Quoted and
// Completed inside In progress, with that state as the detail (the same
// mapping as lib/opportunity). Database enums are unchanged.
// ─────────────────────────────────────────────────────────────────────

import {
  Sparkles, CircleDot, FileText, Check, CalendarDays,
  Flame, Briefcase, Receipt, Trophy,
} from 'lucide-react'
import type { CapabilityName } from '@/lib/permissions'
import { formatCurrency } from '@/utils/formatting'
import type { Stage, StageDef } from './types'
import { PHASE_TONE, type Phase } from '@/lib/phaseTone'

// Lane colors follow the phase rule in lib/phaseTone: one pastel per phase of
// work, so a lead is lavender here, on the Home flow cards and on every chip.
export const STAGES: StageDef[] = [
  { id: 'LEAD',        label: 'Lead',        short: 'Lead',     tone: PHASE_TONE.sales,       nextCta: 'Ready to quote',  next: 'QUALIFIED',  icon: Sparkles,   group: 'sales'   },
  { id: 'QUALIFIED',   label: 'Qualified',   short: 'Qualified', tone: PHASE_TONE.sales,      nextCta: 'Build quote',     next: 'QUOTED',     icon: CircleDot,  group: 'sales'   },
  { id: 'QUOTED',      label: 'Quoted',      short: 'Quoted',   tone: PHASE_TONE.quote,       nextCta: 'Mark approved',   next: 'ACCEPTED',   icon: FileText,   group: 'sales'   },
  { id: 'ACCEPTED',    label: 'Approved',    short: 'Approved', tone: PHASE_TONE.production,  nextCta: 'Schedule job',    next: 'SCHEDULED',  icon: Check,      group: 'work'    },
  { id: 'SCHEDULED',   label: 'Scheduled',   short: 'Scheduled', tone: PHASE_TONE.production, nextCta: 'Start work',      next: 'IN_PROGRESS', icon: CalendarDays, group: 'work' },
  { id: 'IN_PROGRESS', label: 'In progress', short: 'In progress', tone: PHASE_TONE.production, nextCta: 'Mark complete', next: 'COMPLETED',  icon: Flame,      group: 'work'    },
  { id: 'COMPLETED',   label: 'Completed',   short: 'Completed', tone: PHASE_TONE.billing,    nextCta: 'Create invoice',  next: 'INVOICED',   icon: Briefcase,  group: 'billing' },
  { id: 'INVOICED',    label: 'Invoiced',    short: 'Invoiced', tone: PHASE_TONE.billing,     nextCta: 'Record payment',  next: 'PAID',       icon: Receipt,    group: 'billing' },
  { id: 'PAID',        label: 'Paid',        short: 'Paid',     tone: PHASE_TONE.paid,        nextCta: '',                next: null,         icon: Trophy,     group: 'billing' },
]

/**
 * Board stages in each phase of work — what a phase card or ?phase= filters
 * to, and what its figure sums (lib/moneyMetrics boardFigures). An approved
 * quote sits in Quoted (it isn't booked yet); a finished job is billing.
 */
export const PHASE_STAGES: Record<Exclude<Phase, 'late'>, Stage[]> = {
  sales: ['LEAD', 'QUALIFIED'],
  quote: ['QUOTED', 'ACCEPTED'],
  production: ['SCHEDULED', 'IN_PROGRESS'],
  billing: ['COMPLETED', 'INVOICED'],
  paid: ['PAID'],
}

export const STAGE_BY_ID = STAGES.reduce<Record<Stage, StageDef>>((acc, s) => {
  acc[s.id] = s; return acc
}, {} as Record<Stage, StageDef>)

// ─── The 7 stages people see ────────────────────────────────────────────────

export type DisplayStageId = 'LEAD' | 'QUALIFIED' | 'QUOTED' | 'SCHEDULED' | 'IN_PROGRESS' | 'INVOICED' | 'PAID'

export interface DisplayStageDef extends StageDef {
  id: DisplayStageId
  /** The internal states shown in this stage. */
  members: Stage[]
}

const DISPLAY_MEMBERS: Record<DisplayStageId, Stage[]> = {
  LEAD: ['LEAD'],
  QUALIFIED: ['QUALIFIED'],
  QUOTED: ['QUOTED', 'ACCEPTED'],
  SCHEDULED: ['SCHEDULED'],
  IN_PROGRESS: ['IN_PROGRESS', 'COMPLETED'],
  INVOICED: ['INVOICED'],
  PAID: ['PAID'],
}

/** Lead → Qualified → Quoted → Scheduled → In progress → Invoiced → Paid. */
export const DISPLAY_STAGES: DisplayStageDef[] = (Object.keys(DISPLAY_MEMBERS) as DisplayStageId[]).map((id) => ({
  ...STAGE_BY_ID[id],
  id,
  members: DISPLAY_MEMBERS[id],
}))

export const DISPLAY_STAGE_BY_ID = DISPLAY_STAGES.reduce<Record<DisplayStageId, DisplayStageDef>>((acc, s) => {
  acc[s.id] = s; return acc
}, {} as Record<DisplayStageId, DisplayStageDef>)

/** The visible stage an internal state belongs to. */
export function displayStageOf(stage: Stage): DisplayStageDef {
  return DISPLAY_STAGES.find((d) => d.members.includes(stage)) ?? DISPLAY_STAGES[0]
}

/**
 * The in-between states. They sit in a visible stage's column, but a record
 * always reads as its own status: a finished job says "Completed" on the
 * card, the drawer and the journey, the same word search, notifications and
 * the move dialog use (UX-183).
 */
export const STAGE_DETAIL: Partial<Record<Stage, string>> = {
  ACCEPTED: 'Approved',
  COMPLETED: 'Completed',
}

/** What a record's status reads as: "Quoted", "Approved", "Completed". */
export function stageWithDetail(stage: Stage): string {
  return STAGE_DETAIL[stage] ?? displayStageOf(stage).label
}

/** The visible stages for a board limited to `stages` (Crew: job stages). */
export function displayStagesFor(stages?: readonly Stage[]): DisplayStageDef[] {
  return stages ? DISPLAY_STAGES.filter((d) => d.members.some((m) => stages.includes(m))) : DISPLAY_STAGES
}

/** Select or clear every state of a visible stage (the stage filter). */
export function toggleDisplayStage(selected: readonly Stage[], id: DisplayStageId): Stage[] {
  const members = DISPLAY_MEMBERS[id]
  return members.some((m) => selected.includes(m))
    ? selected.filter((s) => !members.includes(s))
    : [...selected, ...members.filter((m) => !selected.includes(m))]
}

/** The one visible stage a filter selection covers, else null. */
export function selectedDisplayStage(selected: readonly Stage[]): DisplayStageId | null {
  const hit = DISPLAY_STAGES.filter((d) => d.members.some((m) => selected.includes(m)))
  return hit.length === 1 ? hit[0].id : null
}

/** Where "Moves to …" lands, in visible-stage words. */
export function nextStepHint(stage: Stage): string | null {
  const next = STAGE_BY_ID[stage].next
  if (!next) return null
  if (displayStageOf(next).id === displayStageOf(stage).id) {
    return next === 'ACCEPTED' ? 'Marks the quote approved' : 'Marks the job completed'
  }
  return `Moves to ${displayStageOf(next).label}`
}

export function stageGroup(stage: StageDef): string {
  switch (stage.group) {
    case 'sales':   return 'Sales'
    case 'work':    return 'Work'
    case 'billing': return 'Billing'
  }
}

export function stageStateLine(r: { stage: Stage; source?: string; budget?: string; quoteNumber?: string; quoteSentAt?: string; acceptedAt?: string; depositPercent?: number; depositAmount?: number; scheduledFor?: string; startedAt?: string; progressPercent?: number; completedAt?: string; invoiceNumber?: string; invoiceDueAt?: string; paidAt?: string }, opts: { money?: boolean } = {}): string {
  // Every field below is optional on the record, and the unified mapper does
  // not populate all of them — `progressPercent` in particular is never mapped,
  // so IN_PROGRESS rendered the literal "undefined% complete" to operators, and
  // a quote with no number rendered "null sent undefined". Drop absent parts
  // instead of interpolating them: a shorter line is always better than a line
  // with "undefined" in it.
  const join = (...parts: Array<string | null | undefined>) =>
    parts.filter((p): p is string => !!p).join(' · ')

  switch (r.stage) {
    // Say only what we know: the real source when one was recorded (UX-065).
    case 'LEAD':         return r.source ? `New lead from ${r.source.toLowerCase()}. Call them back.` : 'New lead. Call them back.'
    case 'QUALIFIED':    return r.budget ? `Ready to quote · budget ${r.budget}` : 'Ready to quote'
    case 'QUOTED':       return join(
      // UX-008: a saved-but-unsent quote is not "sent · awaiting response".
      r.quoteSentAt ? `${r.quoteNumber ?? 'Quote'} sent ${r.quoteSentAt}` : `${r.quoteNumber ?? 'Draft quote'} · not sent yet`,
      r.quoteSentAt ? 'awaiting response' : null,
    )
    case 'ACCEPTED':     return join(
      r.acceptedAt ? `Approved ${r.acceptedAt}` : 'Approved',
      opts.money === false ? null : r.depositAmount != null ? `${formatCurrency(r.depositAmount)} deposit` : r.depositPercent != null ? `${r.depositPercent}% deposit` : null,
    )
    case 'SCHEDULED':    return r.scheduledFor ?? 'Scheduled'
    // Actual start, not the plan (UX-183).
    case 'IN_PROGRESS':  return join(
      r.startedAt ? `Started ${r.startedAt}` : r.scheduledFor ? `Planned for ${r.scheduledFor}` : 'In progress',
      r.progressPercent != null ? `${r.progressPercent}% complete` : null,
    )
    case 'COMPLETED':    return join(
      r.completedAt ? `Finished ${r.completedAt}` : 'Finished',
      // Billing is the office's; Crew get no billing hints (DECISIONS §3).
      opts.money === false ? null : 'ready to bill',
    )
    case 'INVOICED':     return join(r.invoiceNumber ?? 'Invoiced', r.invoiceDueAt)
    case 'PAID':         return r.paidAt ? `Paid ${r.paidAt}` : 'Paid'
  }
}

// ─── Stage moves (UX-129) ───────────────────────────────────────────────────
// A record moves one step at a time, because each step produces something
// real (a quote, a job, an invoice, a payment). Two backward moves exist on
// the server and are offered with a confirm: Qualified → Lead (deal) and
// In progress → Scheduled (undo "Start work"). Everything else is shown but
// disabled with the reason, so the sheet never offers a one-tap jump to Paid.

export type StageMoveKind = 'current' | 'next' | 'back' | 'blocked'

export interface StageMoveOption {
  stage: StageDef
  kind: StageMoveKind
  /** Why a blocked stage can't be picked, in the user's words. */
  reason?: string
}

const BACKWARD_MOVES: Partial<Record<Stage, Stage>> = {
  QUALIFIED: 'LEAD',
  IN_PROGRESS: 'SCHEDULED',
}

export function stageMoveOptions(record: { stage: Stage; id?: string }): StageMoveOption[] {
  const currentIdx = STAGES.findIndex((s) => s.id === record.stage)
  const current = STAGES[currentIdx]
  const next = current.next ? STAGE_BY_ID[current.next] : null
  const back = BACKWARD_MOVES[record.stage]
  return STAGES.map((stage, idx) => {
    if (stage.id === record.stage) return { stage, kind: 'current' }
    if (next && stage.id === next.id) return { stage, kind: 'next' }
    if (back && stage.id === back) return { stage, kind: 'back' }
    if (idx > currentIdx) {
      const reason =
        // Billing stages are reached by doing the thing, not by moving a card.
        stage.id === 'INVOICED' ? 'Create an invoice first'
        : stage.id === 'PAID' ? 'Record a payment first'
        : next ? `One step at a time. First: ${current.nextCta}` : undefined
      return { stage, kind: 'blocked', reason }
    }
    return {
      stage,
      kind: 'blocked',
      reason: record.stage === 'COMPLETED' || idx < currentIdx - 1
        ? `Can't move back to ${stage.label}`
        : `Can't move back to ${stage.label} from here`,
    }
  })
}

/** How a drop / pick of `target` should be handled for a record. */
export function classifyStageMove(record: { stage: Stage }, target: Stage): StageMoveOption {
  return stageMoveOptions(record).find((o) => o.stage.id === target)!
}

/**
 * The internal state a drop on (or pick of) a visible stage means: the next
 * step or the allowed step back when it lies in that stage, null for the
 * card's own stage, else the stage itself (which classifies as blocked, with
 * the reason).
 */
export function moveTargetFor(record: { stage: Stage }, id: DisplayStageId): Stage | null {
  const members = DISPLAY_MEMBERS[id]
  if (members.includes(record.stage)) return null
  const next = STAGE_BY_ID[record.stage].next
  if (next && members.includes(next)) return next
  const back = BACKWARD_MOVES[record.stage]
  if (back && members.includes(back)) return back
  return id
}

export interface DisplayMoveOption {
  stage: DisplayStageDef
  kind: StageMoveKind
  reason?: string
  /** The internal state to move to (null for the current stage). */
  target: Stage | null
}

/** "Move to stage" over the 7 visible stages. */
export function displayMoveOptions(record: { stage: Stage }): DisplayMoveOption[] {
  return DISPLAY_STAGES.map((stage) => {
    const target = moveTargetFor(record, stage.id)
    if (!target) return { stage, kind: 'current', target: null }
    const o = classifyStageMove(record, target)
    return { stage, kind: o.kind, reason: o.reason, target }
  })
}

// ─── Who may move a record (F1 capabilities) ────────────────────────────────
/** The capability each stage's outcome action needs (Start work → jobs.complete). */
export const STAGE_ADVANCE_CAPABILITY: Record<Stage, CapabilityName | null> = {
  LEAD: 'opportunities.edit',
  QUALIFIED: 'quotes.create',
  QUOTED: 'quotes.create',
  ACCEPTED: 'jobs.edit',
  SCHEDULED: 'jobs.complete',
  IN_PROGRESS: 'jobs.complete',
  COMPLETED: 'invoices.create',
  INVOICED: 'payments.record',
  PAID: null,
}

/**
 * What editing, archiving or deleting a record at each stage needs: the
 * record that stage is about (opportunity, quote, job or invoice). Crew can
 * start and finish jobs but not edit or delete them.
 */
export const STAGE_EDIT_CAPABILITY: Record<Stage, CapabilityName> = {
  LEAD: 'opportunities.edit',
  QUALIFIED: 'opportunities.edit',
  QUOTED: 'quotes.create',
  ACCEPTED: 'quotes.create',
  SCHEDULED: 'jobs.edit',
  IN_PROGRESS: 'jobs.edit',
  COMPLETED: 'jobs.edit',
  INVOICED: 'invoices.create',
  PAID: 'invoices.create',
}

/** The capability a step back to `to` needs. */
export function stepBackCapability(to: Stage): CapabilityName {
  return to === 'LEAD' ? 'opportunities.edit' : 'jobs.complete'
}
